import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, finalize, of, tap } from 'rxjs';

import { CalendarRoomRow, RoleType, RoomServicesEnum } from '@bookly/shared';

import { RoomsCalendarApiService } from './rooms-calendar-api.service';
import { EventCalendarFiltersEnum } from '../model/rooms-calendar.model';

export interface CalendarFilters {
  status?: EventCalendarFiltersEnum;
  service?: RoomServicesEnum[];
  siteName?: string[];
  buildingName?: string[];
  floorName?: string[];
  sort: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root',
})
export class RoomsCalendarService {
  readonly #roomsCalendarApiService = inject(RoomsCalendarApiService);
  readonly #isRoomsCalendarDataLoading = signal(false);
  readonly #roomsCalendarData = signal<CalendarRoomRow[]>([]);
  readonly #roomsCalendarDataSnapshot = signal<CalendarRoomRow[]>([]);
  readonly #calendarFiltersSignal = signal<CalendarFilters>({
    status: EventCalendarFiltersEnum.ALL,
    sort: 'asc',
  });
  readonly #siteNamesSignal = computed(() => {
    const rooms = this.#roomsCalendarDataSnapshot();
    if (!rooms) {
      return [];
    }
    return Array.from(
      new Set(rooms.map(room => room.siteName).filter(Boolean))
    ).sort();
  });

  readonly #buildingNamesSignal = computed(() => {
    const rooms = this.#roomsCalendarDataSnapshot();
    if (!rooms) {
      return [];
    }
    const filters = this.#calendarFiltersSignal();
    const filtered = filters.siteName?.length
      ? rooms.filter(room => filters.siteName!.includes(room.siteName))
      : rooms;
    return Array.from(
      new Set(filtered.map(room => room.buildingName).filter(Boolean))
    ).sort();
  });

  readonly #floorNamesSignal = computed(() => {
    const rooms = this.#roomsCalendarDataSnapshot();
    if (!rooms) {
      return [];
    }
    const filters = this.#calendarFiltersSignal();
    let filtered = rooms;
    if (filters.siteName?.length) {
      filtered = filtered.filter(room =>
        filters.siteName!.includes(room.siteName)
      );
    }
    if (filters.buildingName?.length) {
      filtered = filtered.filter(room =>
        filters.buildingName!.includes(room.buildingName)
      );
    }
    return Array.from(
      new Set(filtered.map(room => room.floorName).filter(Boolean))
    ).sort();
  });

  get isRoomsCalendarDataLoading() {
    return this.#isRoomsCalendarDataLoading.asReadonly();
  }

  get roomsCalendarData() {
    return this.#roomsCalendarData.asReadonly();
  }

  get siteNames() {
    return this.#siteNamesSignal;
  }

  get buildingNames() {
    return this.#buildingNamesSignal;
  }

  get floorNames() {
    return this.#floorNamesSignal;
  }

  public setCalendarFilters(filters: CalendarFilters) {
    this.#calendarFiltersSignal.set(filters);
  }

  public clearCalendarFilters() {
    this.#calendarFiltersSignal.set({
      status: EventCalendarFiltersEnum.ALL,
      sort: 'asc',
    });
  }

  public getRoomsEvents(
    roleType: RoleType | null,
    dateFrom: string,
    dateTo: string
  ): Observable<CalendarRoomRow[]> {
    if (roleType) {
      this.#isRoomsCalendarDataLoading.set(true);
      return this.#roomsCalendarApiService
        .getRoomsEvents(roleType, dateFrom, dateTo)
        .pipe(
          tap(roomsCalendarData => {
            this.#roomsCalendarDataSnapshot.set(roomsCalendarData);
            this.filterEvents(this.#calendarFiltersSignal());
          }),
          finalize(() => this.#isRoomsCalendarDataLoading.set(false))
        );
    }
    return of([]);
  }

  public filterEvents(calendarFilters: CalendarFilters) {
    let filteredEvents;
    if (
      calendarFilters.status === EventCalendarFiltersEnum.ALL &&
      (!calendarFilters.service || calendarFilters.service.length === 0)
    ) {
      filteredEvents = this.#roomsCalendarDataSnapshot();
    } else {
      filteredEvents = this.#roomsCalendarDataSnapshot().map(roomEvents => ({
        ...roomEvents,
        events: roomEvents.events.filter(event => {
          const statusMatch =
            calendarFilters.status === EventCalendarFiltersEnum.ALL ||
            calendarFilters.status === event.status;

          const serviceMatch =
            !calendarFilters.service?.length ||
            event.services.some(service =>
              calendarFilters.service!.includes(service.serviceType)
            );

          return statusMatch && serviceMatch;
        }),
      }));
    }

    filteredEvents = filteredEvents.filter(event => {
      const siteNameMatch =
        !calendarFilters.siteName?.length ||
        calendarFilters.siteName.includes(event.siteName);
      const buildingNameMatch =
        !calendarFilters.buildingName?.length ||
        calendarFilters.buildingName.includes(event.buildingName);
      const floorNameMatch =
        !calendarFilters.floorName?.length ||
        calendarFilters.floorName.includes(event.floorName);

      return siteNameMatch && buildingNameMatch && floorNameMatch;
    });
    if (calendarFilters.sort === 'asc') {
      filteredEvents = filteredEvents.sort((a, b) =>
        a.roomName.localeCompare(b.roomName)
      );
    } else {
      filteredEvents = filteredEvents.sort((a, b) =>
        b.roomName.localeCompare(a.roomName)
      );
    }
    this.#roomsCalendarData.set(filteredEvents);
  }
}
